import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Scale, X, ArrowRight } from 'lucide-react';
import { mediaUrl } from '@/lib/api';
import { useCompare } from '@/lib/compareStore';

/**
 * Floating compare tray — the whole interaction model for the feature.
 * Appears the moment something is shortlisted, states the limit plainly,
 * and never blocks the page. Hidden on the comparison page itself.
 */
export const CompareTray = () => {
  const { items, remove, clear, maxColumns } = useCompare();
  const { pathname } = useLocation();

  if (!items.length) return null;
  if (pathname === '/compare' || pathname.startsWith('/compare/')) return null;

  return (
    <div
      className='fixed inset-x-0 bottom-0 z-40 px-3 pb-3 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:px-0 pointer-events-none'
      data-testid='compare-tray'
    >
      <div className='pointer-events-auto mx-auto flex max-w-3xl items-center gap-3 rounded-xl border bg-background/95 p-2.5 shadow-lg shadow-black/10 backdrop-blur sm:rounded-full sm:pl-4'>
        <div className='hidden sm:flex items-center gap-1.5 text-sm font-semibold shrink-0'>
          <Scale className='h-4 w-4 text-primary' />
          <span data-testid='compare-tray-count'>{items.length} of {maxColumns}</span>
        </div>
        <div className='flex flex-1 items-center gap-2 overflow-x-auto'>
          {items.map((i) => (
            <div
              key={i.id}
              className='relative h-11 w-11 shrink-0 overflow-hidden rounded-md border bg-muted'
              title={i.title}
              data-testid={`compare-tray-item-${i.id}`}
            >
              {i.thumb_url ? (
                <img src={mediaUrl(i.thumb_url)} alt={i.title} className='h-full w-full object-cover' />
              ) : (
                <div className='flex h-full w-full items-center justify-center text-[9px] text-muted-foreground'>No image</div>
              )}
              <button
                type='button'
                onClick={() => remove(i.id)}
                className='absolute right-0 top-0 rounded-bl bg-background/90 p-0.5 text-muted-foreground hover:text-destructive'
                aria-label={`Remove ${i.title} from comparison`}
                data-testid={`compare-tray-remove-${i.id}`}
              >
                <X className='h-3 w-3' />
              </button>
            </div>
          ))}
        </div>
        <div className='flex shrink-0 items-center gap-2'>
          <span className='sm:hidden text-xs font-semibold text-muted-foreground' data-testid='compare-tray-count-mobile'>
            {items.length}/{maxColumns}
          </span>
          <button
            type='button'
            onClick={clear}
            className='text-xs text-muted-foreground hover:text-foreground'
            data-testid='compare-tray-clear'
          >
            Clear
          </button>
          <Link
            to='/compare'
            className='inline-flex h-9 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90'
            data-testid='compare-tray-compare-btn'
          >
            Compare <ArrowRight className='h-3.5 w-3.5' />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CompareTray;
